import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Loader2, Download, Trophy, AlertTriangle, Search } from 'lucide-react';
import { API } from '../config/api';

const CATEGORY_LABELS = {
  turismo_pista: 'Turismo Pista',
  carshow_santafesino: 'CarShow Santafesino',
  turismo_nacional: 'Turismo Nacional',
  simracing: 'Simracing',
};

/**
 * Modal that links an analysis race_session to a single tanda of a Pit
 * Engineer event. Flow: pick a session → confirm matched driver → pick a
 * reference driver → preview → apply.
 */
export default function AnalysisImportDialog({
  open,
  onOpenChange,
  eventId,
  eventDate,
  tandaKey,
  tandaLabel,
  pilotName,
  eventCategory,
  eventCircuit,
  onImported,
}) {
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [pilotDriverNumber, setPilotDriverNumber] = useState(null);
  const [refDriverNumber, setRefDriverNumber] = useState(null);
  const [importing, setImporting] = useState(false);
  // Lap-count handling. null = auto-detect, true = file already has slow laps,
  // false = file is "clean" so we add 3/2 forced slow laps.
  const [fileIncludesSlow, setFileIncludesSlow] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelectedSession(null);
    setDrivers([]);
    setPilotDriverNumber(null);
    setRefDriverNumber(null);
    loadSessions();
  }, [open]);

  const loadSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/pit/analysis/sessions`, { withCredentials: true });
      setSessions(res.data.sessions || []);
    } catch (err) {
      toast.error('No se pudieron cargar las sesiones');
    } finally {
      setLoadingSessions(false);
    }
  };

  // Filter + auto-prioritize sessions matching the event's category and circuit
  const filteredSessions = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    const list = sessions.filter((s) => {
      if (!q) return true;
      const blob = `${s.name || ''} ${s.circuit || ''} ${s.date || ''}`.toLowerCase();
      return blob.includes(q);
    });
    return list.slice().sort((a, b) => {
      const aMatchCat = (a.category || '').toLowerCase().includes((eventCategory || '').toLowerCase()) ? 0 : 1;
      const bMatchCat = (b.category || '').toLowerCase().includes((eventCategory || '').toLowerCase()) ? 0 : 1;
      if (aMatchCat !== bMatchCat) return aMatchCat - bMatchCat;
      return (b.date || '').localeCompare(a.date || '');
    });
  }, [sessions, filterText, eventCategory]);

  const pickSession = async (session) => {
    setSelectedSession(session);
    setDrivers([]);
    setPilotDriverNumber(null);
    setRefDriverNumber(null);
    setLoadingDrivers(true);
    try {
      const res = await axios.get(
        `${API}/pit/analysis/sessions/${session.id}/drivers`,
        { params: { pilot_name: pilotName }, withCredentials: true },
      );
      const d = res.data.drivers || [];
      setDrivers(d);
      // Auto-select the best similarity match if any
      const auto = res.data.auto_match;
      if (auto) {
        setPilotDriverNumber(auto.driver_number);
      }
    } catch (err) {
      toast.error('Error al cargar los pilotos de la sesión');
      setSelectedSession(null);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const matchedDriver = useMemo(
    () => drivers.find((d) => d.driver_number === pilotDriverNumber) || null,
    [drivers, pilotDriverNumber],
  );

  const refDriver = useMemo(
    () => drivers.find((d) => d.driver_number === refDriverNumber) || null,
    [drivers, refDriverNumber],
  );

  const fastSlow = useMemo(() => {
    if (!matchedDriver || !matchedDriver.lap_count) return null;
    // Frontend doesn't have raw lap times; show lap_count + position for preview only.
    return { laps: matchedDriver.lap_count, position: matchedDriver.position };
  }, [matchedDriver]);

  const apply = async () => {
    if (!selectedSession || !matchedDriver) {
      toast.error('Tenés que elegir la sesión y el piloto');
      return;
    }
    setImporting(true);
    try {
      const body = {
        tanda_key: tandaKey,
        race_id: selectedSession.id,
        driver_number: matchedDriver.driver_number,
        ref_driver_number: refDriverNumber || null,
        file_includes_slow_laps: fileIncludesSlow,
      };
      const res = await axios.post(
        `${API}/pit/events/${eventId}/import-analysis`,
        body,
        { withCredentials: true },
      );
      const imp = res.data.imported || {};
      let importedTanda = res.data.tanda;
      toast.success(`Datos importados: ${imp.fast_laps || 0} rápidas, ${imp.slow_laps || 0} lentas, posición ${imp.position || '—'}`);

      // If the analysis file carried an hour and we now have eventDate +
      // tanda_time but no weather yet, automatically fetch the historical
      // weather and persist it. Best-effort — failures are logged silently.
      const importedTime = importedTanda?.tanda_time;
      const targetDate = eventDate || selectedSession.date;
      if (importedTime && targetDate && !importedTanda?.weather && navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 8000, maximumAge: 60000 });
          });
          const weatherRes = await axios.get(`${API}/pit/weather/historical`, {
            params: {
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
              date: targetDate,
              time: importedTime,
            },
            withCredentials: true,
          });
          // PATCH the tanda with the captured weather so it persists.
          const patchBody = { tandas: { [tandaKey]: { weather: weatherRes.data } } };
          const patchRes = await axios.patch(
            `${API}/pit/events/${eventId}`,
            patchBody,
            { withCredentials: true },
          );
          importedTanda = patchRes.data.event?.tandas?.[tandaKey] || importedTanda;
          toast.success(`Clima histórico ${importedTime}: ${weatherRes.data.temp}°C`, { duration: 1800 });
        } catch (wErr) {
          // Silent — keep the import successful even if weather lookup fails.
          // (Common reasons: user denied geolocation, date out of Open-Meteo's
          //  90-day historical window.)
          // eslint-disable-next-line no-console
          console.warn('Weather auto-fetch failed:', wErr?.response?.data?.detail || wErr?.message);
        }
      }

      onImported?.(importedTanda);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar los datos');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-neutral-900 border-neutral-800 text-white max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-4 h-4 text-blue-400" />
            Traer datos de análisis — <span className="text-orange-400">{tandaLabel}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Linkeá esta tanda con una sesión ya importada para autocompletar mejor tiempo, vueltas rápidas/lentas, posición y referencia.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Step 1: Pick a session */}
          {!selectedSession && (
            <div className="space-y-2">
              <Label className="text-xs text-gray-400">1. Elegí la sesión</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-3 text-gray-500" />
                <Input
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Buscar por nombre, circuito o fecha..."
                  data-testid="analysis-session-filter"
                  className="bg-neutral-800 border-neutral-700 text-white pl-8 h-9 text-sm"
                />
              </div>
              {loadingSessions ? (
                <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando sesiones...
                </div>
              ) : filteredSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <AlertTriangle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No hay sesiones disponibles. Importá una primero desde el dashboard.
                </div>
              ) : (
                <div className="border border-neutral-800 rounded-md max-h-[40vh] overflow-y-auto">
                  {filteredSessions.map((s) => {
                    const matchesCat = (s.category || '').toLowerCase().includes((eventCategory || '').toLowerCase());
                    const matchesCircuit = (s.circuit || '').toLowerCase().includes((eventCircuit || '').toLowerCase().split(' ').pop() || '');
                    return (
                      <button
                        key={s.id}
                        onClick={() => pickSession(s)}
                        data-testid={`analysis-pick-session-${s.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-neutral-800 border-b border-neutral-800/40 transition flex items-center gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white truncate flex items-center gap-2">
                            {s.name || 'Sin nombre'}
                            {matchesCat && (
                              <Badge className="bg-green-700 text-[9px] h-4 px-1.5">categoría</Badge>
                            )}
                            {matchesCircuit && (
                              <Badge className="bg-blue-700 text-[9px] h-4 px-1.5">autódromo</Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-gray-500 flex gap-2 flex-wrap">
                            <span>{s.date || '—'}</span>
                            <span>·</span>
                            <span>{s.circuit || '—'}</span>
                            {s.category && (
                              <>
                                <span>·</span>
                                <span>{CATEGORY_LABELS[s.category] || s.category}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Driver selection + preview */}
          {selectedSession && (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs">
                  <div className="text-gray-500">Sesión seleccionada</div>
                  <div className="text-white font-semibold">{selectedSession.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {selectedSession.date} · {selectedSession.circuit} · {CATEGORY_LABELS[selectedSession.category] || selectedSession.category || '—'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSelectedSession(null); setDrivers([]); }}
                  className="text-xs"
                >
                  Cambiar
                </Button>
              </div>

              {loadingDrivers ? (
                <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando pilotos...
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-gray-400">2. Piloto del evento — <span className="text-orange-400">{pilotName}</span></Label>
                    <select
                      value={pilotDriverNumber ?? ''}
                      onChange={(e) => setPilotDriverNumber(e.target.value === '' ? null : Number(e.target.value))}
                      data-testid="analysis-driver-select"
                      className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10 mt-1"
                    >
                      <option value="">Seleccioná un piloto...</option>
                      {drivers.map((d) => (
                        <option key={d.driver_number} value={d.driver_number}>
                          #{d.driver_number} — {d.driver_name} ({d.best_lap_str || 'sin vueltas'}) {d.similarity > 0.5 ? '✓ match' : ''}
                        </option>
                      ))}
                    </select>
                    {matchedDriver && matchedDriver.similarity < 0.5 && (
                      <div className="mt-1 text-[11px] text-yellow-400 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        El nombre no coincide del todo — confirmá que sea el piloto correcto.
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs text-gray-400">3. Tiempo de referencia (opcional)</Label>
                    <select
                      value={refDriverNumber ?? ''}
                      onChange={(e) => setRefDriverNumber(e.target.value === '' ? null : Number(e.target.value))}
                      data-testid="analysis-ref-driver-select"
                      className="w-full bg-neutral-800 border border-neutral-700 text-white rounded-md px-3 py-2 text-sm h-10 mt-1"
                    >
                      <option value="">Pole de la sesión (vuelta más rápida)</option>
                      {drivers.filter((d) => d.best_lap !== null).map((d) => (
                        <option key={d.driver_number} value={d.driver_number}>
                          #{d.driver_number} — {d.driver_name} ({d.best_lap_str})
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Por defecto se toma la pole de la sesión. Podés elegir cualquier piloto como referencia.
                    </p>
                  </div>

                  {/* Preview */}
                  {matchedDriver && (
                    <div className="bg-neutral-800/40 border border-neutral-700 rounded-md p-3 space-y-2">
                      <div className="text-xs font-semibold text-white flex items-center gap-1.5">
                        <Trophy className="w-3.5 h-3.5 text-yellow-400" /> Vista previa
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-gray-500">Mejor tiempo:</span> <span className="text-green-400 font-mono">{matchedDriver.best_lap_str || '—'}</span></div>
                        <div><span className="text-gray-500">Posición:</span> <span className="text-white font-mono">{matchedDriver.position || '—'}</span></div>
                        <div><span className="text-gray-500">Vueltas en archivo:</span> <span className="text-white font-mono">{matchedDriver.lap_count}</span></div>
                        <div><span className="text-gray-500">Referencia:</span> <span className="text-blue-400 font-mono">{refDriver ? refDriver.best_lap_str : (drivers[0]?.best_lap_str || '—')}</span></div>
                      </div>
                      <div className="pt-2 border-t border-neutral-700/50">
                        <Label className="text-[11px] text-gray-400">Tipo de archivo (conteo de vueltas)</Label>
                        <select
                          value={fileIncludesSlow === null ? 'auto' : (fileIncludesSlow ? 'all' : 'clean')}
                          onChange={(e) => {
                            const v = e.target.value;
                            setFileIncludesSlow(v === 'auto' ? null : v === 'all');
                          }}
                          data-testid="file-type-select"
                          className="w-full bg-neutral-900 border border-neutral-700 text-white text-xs rounded px-2 py-1.5 mt-1"
                        >
                          <option value="auto">Auto-detectar (recomendado)</option>
                          <option value="clean">El archivo SÓLO tiene rápidas (sumar 3 lentas para serie/final, 2 para resto)</option>
                          <option value="all">El archivo incluye out/previa/honor (no sumar extras)</option>
                        </select>
                      </div>
                      <div className="text-[10px] text-gray-500 pt-1">
                        Las vueltas rápidas/lentas se calculan en el servidor con umbral 115% del best del piloto.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={importing}>
            Cancelar
          </Button>
          <Button
            onClick={apply}
            disabled={importing || !selectedSession || !matchedDriver}
            data-testid="analysis-import-apply"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
